import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { c, tracked } from '../theme';
import { Shell, Wordmark } from '../components/Brand';
import { AuthScene, PillChip, PillField, SceneHeadline, SceneLead, SceneLink, VioletCTA } from '../components/AuthScene';
import { Menu } from '../components/Menu';
import { api, ApiError, type BuyerFit as Answers, type HouseAnswers, type ReportSummary, type User } from '../api';
import { isNative } from '../native';

/**
 * Step 3 of the new-report flow: what the buyer is looking for, asked fresh per
 * report, plus the price they are working from. Built to the auth-flow
 * handoff's Buyer Fit v2 — light scrim over the clip, frosted chips, violet
 * selection — without its "Where are you up to?" group (review comment #7).
 *
 * The caps are enforced in silence. Past the limit the unpicked chips in that
 * group mute and stop responding, which is the only signal the design gives.
 */
type ChipKey = 'buying' | 'matters' | 'mustHaves' | 'stage';
type Group = {
  key: ChipKey;
  label: string;
  cap: number;
  tone: string;
  chips: string[];
};

const GROUPS: Group[] = [
  {
    key: 'buying',
    label: 'Who’s Buying?',
    cap: 1,
    tone: c.red,
    chips: ['just me', 'couple', 'family with kids', 'downsizing', 'investing'],
  },
  {
    key: 'matters',
    label: 'What Matters Most?',
    cap: 8,
    tone: c.violet,
    chips: [
      'walkability',
      'public transport',
      'good schools',
      'parks & green space',
      'cafés & dining',
      'shops & groceries',
      'safety',
      'close to the city',
      'low natural hazard risk',
      'future growth',
      'rental return',
    ],
  },
  {
    key: 'mustHaves',
    label: 'Your Must-Haves',
    cap: 5,
    tone: c.cyan,
    chips: [
      '3+ bedrooms',
      '4+ bedrooms',
      '2+ bathrooms',
      'off-street parking',
      'home office / study',
      'outdoor space',
      'pool',
      'single level',
    ],
  },

];


/** About the house itself — what no sales feed can see. Asked in this order; one answer each. */
type HouseKey = keyof HouseAnswers;
const HOUSE_QUESTIONS: { key: HouseKey; label: string; options: [string, string][] }[] = [
  { key: 'finish', label: 'What Condition Is the Property In?', options: [['needs_work', 'Needs Work'], ['original', 'Original'], ['updated', 'Updated'], ['high_spec', 'High-Spec Renovation'], ['new_build', 'New or Rebuilt']] },
  { key: 'changed_since_sale', label: 'Was That Work Done After It Last Sold?', options: [['yes', 'Yes'], ['no', 'No'], ['unknown', 'Don’t Know']] },
  { key: 'extra', label: 'Anything Extra the Bedroom Count Doesn’t Show?', options: [['none', 'Nothing'], ['extension', 'Extra Living / Extension'], ['granny', 'Granny Flat or Secondary Dwelling'], ['pool', 'Pool']] },
];

const EMPTY: Answers = { buying: [], matters: [], mustHaves: [], stage: [] };

export function BuyerFit({
  user,
  reports,
}: {
  user: User | null;
  reports: ReportSummary[];
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const picked = (location.state ?? null) as { gnafId: string; displayName: string } | null;

  const [answers, setAnswers] = useState<Answers>(EMPTY);
  // Where it is left blank the advertised price is used.
  const [price, setPrice] = useState('');
  const [source, setSource] = useState<'agent' | 'buyer'>('agent');
  const [house, setHouse] = useState<HouseAnswers>({});
  // Only asked when the finish says work was done; otherwise it is stored as null.
  const askChanged = house.finish === 'updated' || house.finish === 'high_spec' || house.finish === 'new_build';
  const pickHouse = (key: HouseKey, value: string) =>
    setHouse((prev) => {
      const next = { ...prev, [key]: prev[key] === value ? null : value } as HouseAnswers;
      if (key === 'finish' && !(next.finish === 'updated' || next.finish === 'high_spec' || next.finish === 'new_build')) next.changed_since_sale = null;
      return next;
    });
  const houseAnswers = (): HouseAnswers | null => {
    const h = { ...house, changed_since_sale: askChanged ? house.changed_since_sale ?? null : null };
    return Object.values(h).some((v) => v) ? h : null;
  };
  const withExtras = (fit: Answers): Answers => ({
    ...fit,
    ...(priceValue ? { indicativePrice: priceValue, indicativeSource: source } : null),
    ...(houseAnswers() ? { house: houseAnswers() } : null),
  });
  const priceValue = (() => {
    const n = Number(price.replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(n) || n <= 0) return null;
    // "1.6" or "1.6m" means millions; "850k" means thousands.
    if (/m\s*$/i.test(price) || n < 100) return Math.round(n * 1_000_000);
    if (/k\s*$/i.test(price) || n < 100_000) return Math.round(n * 1_000);
    return Math.round(n);
  })();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const count = useMemo(() => GROUPS.reduce((n, g) => n + answers[g.key].length, 0), [answers]);

  // Nothing to assess without an address, which happens on a refresh — the pick
  // lives in history state rather than the URL.
  if (!picked) return <Missing onBack={() => navigate('/search', { replace: true })} />;

  const toggle = (g: Group, chip: string) => {
    setAnswers((prev) => {
      const chosen = prev[g.key];
      if (chosen.includes(chip)) return { ...prev, [g.key]: chosen.filter((x) => x !== chip) };
      if (g.cap === 1) return { ...prev, [g.key]: [chip] };
      if (chosen.length >= g.cap) return prev;
      return { ...prev, [g.key]: [...chosen, chip] };
    });
  };

  const start = async (fit: Answers) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // Sent with the report rather than after it: the Buyer Fit module is
      // built during assembly, so answers that arrive later arrive too late.
      const { id } = await api.createReport(picked.gnafId, picked.displayName, fit);
      navigate(`/assembling/${id}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start the report.');
      setBusy(false);
    }
  };

  return (
    <AuthScene scrim="light">
      <div style={{ paddingTop: 48 }}>
        <div style={{ padding: '0 var(--gx)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <BackButton onClick={() => navigate('/search')} />
            <Wordmark />
            <Menu current="" user={user} assembling={null} reportCount={reports.length} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 48 }}>
            <SceneHeadline>
              Buyer Fit <span style={{ fontWeight: 700 }}>Assessment</span>
            </SceneHeadline>
            <SceneLead style={{ fontSize: 16, lineHeight: 1.4, letterSpacing: '-0.01em', maxWidth: 310 }}>
              What matters to you for this property — we’ll show where it fits you well and where it asks you to compromise.
            </SceneLead>
          </div>
        </div>

        <div style={{ padding: '0 var(--gx)', display: 'flex', flexDirection: 'column' }}>
          {GROUPS.map((g) => {
            const chosen = answers[g.key];
            const full = chosen.length >= g.cap;
            return (
              <div key={g.label} style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 30 }}>
                <div style={{ fontSize: 13, letterSpacing: '0.02em', fontWeight: 700, color: c.ink, textAlign: 'center' }}>{g.label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {g.chips.map((chip) => {
                    const on = chosen.includes(chip);
                    const locked = full && !on && g.cap > 1;
                    return (
                      <button
                        key={chip}
                        type="button"
                        disabled={locked}
                        aria-pressed={on}
                        onClick={() => toggle(g, chip)}
                        style={{
                          flex: '1 1 auto',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 9,
                          height: 48,
                          padding: '0 16px',
                          boxSizing: 'border-box',
                          borderRadius: 999,
                          border: `1px solid ${on ? c.violet : locked ? 'rgba(245,238,219,0.4)' : 'rgba(245,238,219,0.8)'}`,
                          background: on ? c.violet : locked ? 'rgba(245,238,219,0.3)' : 'rgba(245,238,219,0.62)',
                          color: on ? c.cream : locked ? 'rgba(31,26,31,0.35)' : c.ink,
                          fontSize: 13,
                          fontWeight: 500,
                          lineHeight: 1,
                          whiteSpace: 'nowrap',
                          textTransform: 'capitalize',
                          cursor: locked ? 'default' : 'pointer',
                        }}
                      >
                        <span style={{ width: 6, height: 6, borderRadius: '50%', flex: 'none', background: on ? c.cream : locked ? 'rgba(31,26,31,0.1)' : 'rgba(31,26,31,0.2)' }} />
                        {chip}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* About the house itself — it adjusts the comparable sales, never silently. */}
          <div style={{ paddingTop: 40, display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>About the Property</div>
            <div style={{ fontSize: 12, lineHeight: 1.5, color: 'rgba(31,26,31,0.6)' }}>
              What the sales records can’t see. We adjust the comparable sales by what you tell us, and show the working.
            </div>
          </div>
          {HOUSE_QUESTIONS.filter((q) => q.key !== 'changed_since_sale' || askChanged).map((q) => (
            <div key={q.key} style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 26 }}>
              <div style={{ fontSize: 13, letterSpacing: '0.02em', fontWeight: 700, color: c.ink, textAlign: 'center' }}>{q.label}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {q.options.map(([value, label]) => {
                  const on = house[q.key] === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={on}
                      onClick={() => pickHouse(q.key, value)}
                      style={{
                        flex: '1 1 auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 9,
                        height: 48,
                        padding: '0 16px',
                        boxSizing: 'border-box',
                        borderRadius: 999,
                        border: `1px solid ${on ? c.violet : 'rgba(245,238,219,0.8)'}`,
                        background: on ? c.violet : 'rgba(245,238,219,0.62)',
                        color: on ? c.cream : c.ink,
                        fontSize: 13,
                        fontWeight: 500,
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: '50%', flex: 'none', background: on ? c.cream : 'rgba(31,26,31,0.2)' }} />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* The price the buyer is working from (review comments #5, #6). */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 30 }}>
            <label htmlFor="indicative-price" style={{ fontSize: 13, letterSpacing: '0.02em', fontWeight: 700, color: c.ink, textAlign: 'center' }}>
              Indicative Price / Price I’m Considering
            </label>
            <PillField
              id="indicative-price"
              dot={c.magenta}
              inputMode="decimal"
              placeholder="e.g. 1.6m"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              trailing={priceValue != null ? <PillChip>{`$${priceValue.toLocaleString('en-AU')}`}</PillChip> : null}
            />
            {priceValue != null && (
              <div style={{ display: 'flex', gap: 8 }}>
                {([['agent', 'The Agent’s Guide'], ['buyer', 'My Own Figure']] as const).map(([k, l]) => (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={source === k}
                    onClick={() => setSource(k)}
                    style={{
                      flex: 1,
                      height: 48,
                      borderRadius: 999,
                      border: `1px solid ${source === k ? c.violet : 'rgba(245,238,219,0.8)'}`,
                      background: source === k ? c.violet : 'rgba(245,238,219,0.62)',
                      color: source === k ? c.cream : c.ink,
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: 40, padding: '0 var(--gx) 44px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={{ fontSize: 22, letterSpacing: '0.01em', whiteSpace: 'nowrap', fontWeight: 500 }}>
              <span style={{ fontWeight: 700 }}>{count}</span> selected
            </div>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(31,26,31,0.6)', whiteSpace: 'nowrap', ...tracked(0.16) }}>STEP 3 OF 3</div>
          </div>
          <div style={{ fontSize: 11, lineHeight: 1.55, color: 'rgba(31,26,31,0.6)' }}>
            Your answers about the property adjust the comparable sales. The rest weight the report’s scoring.
          </div>
          {user?.allowance?.limit != null && (
            <div style={{ fontSize: 12, lineHeight: 1.5, fontWeight: 600, color: c.violet }}>
              {isNative
                ? `Continuing starts the report and uses 1 of your reports for ${picked.displayName}.`
                : <>Continuing starts the report and uses your beta report{user.allowance.limit === 1 ? '' : ' allowance'} for {picked.displayName}.</>}
            </div>
          )}
          {error && <div style={{ fontSize: 12, color: c.red }}>{error}</div>}
          <VioletCTA disabled={busy} onClick={() => start(withExtras(answers))}>
            {busy ? 'starting…' : 'continue'}
          </VioletCTA>
          <div style={{ textAlign: 'center' }}>
            <SceneLink onClick={() => !busy && start(withExtras(EMPTY))}>
              SKIP — I’LL KEEP IT GENERAL
            </SceneLink>
          </div>
        </div>
      </div>
    </AuthScene>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="Back"
      onClick={onClick}
      style={{
        width: 36,
        height: 36,
        margin: '-6px 0 -6px -8px',
        borderRadius: '50%',
        border: '1px solid rgba(245,238,219,0.8)',
        background: 'rgba(245,238,219,0.62)',
        color: c.ink,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M8.5 3L4 7l4.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function Missing({ onBack }: { onBack: () => void }) {
  return (
    <Shell>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '60px 0' }}>
        <div style={{ fontSize: 24, fontWeight: 700 }}>Pick an address first</div>
        <div style={{ fontSize: 13, color: c.ink6 }}>
          The assessment is asked against a particular property, and that choice was lost on refresh.
        </div>
        <button
          type="button"
          onClick={onBack}
          style={{
            alignSelf: 'flex-start',
            padding: '14px 22px',
            borderRadius: 999,
            border: `1px solid ${c.redLine}`,
            background: 'none',
            color: c.red,
            font: 'inherit',
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
            ...tracked(0.14),
          }}
        >
          BACK TO SEARCH
        </button>
      </div>
    </Shell>
  );
}
